import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const RecommendationCard = styled(Card)(({ theme, priority }) => ({
  border: `2px solid ${
    priority === 'Yüksek' ? theme.palette.error.main :
    priority === 'Orta' ? theme.palette.warning.main :
    theme.palette.success.main
  }`,
  backgroundColor: priority === 'Yüksek' ? theme.palette.error.light :
                  priority === 'Orta' ? theme.palette.warning.light :
                  theme.palette.success.light,
  marginBottom: theme.spacing(2),
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(1),
}));

const TourismRecommendations = ({ calculation, history }) => {
  const [expandedSections, setExpandedSections] = useState({
    current: true,
    general: true,
    industry: false,
    trends: false
  });

  // Kategori ikonları
  const categoryIcons = {
    'Enerji': '⚡',
    'Su': '💧',
    'Yemek & İçecek': '🍽️',
    'Ulaşım': '🚗',
    'Atık': '🗑️',
    'Konaklama': '🏨',
    'Tesis Hizmetleri': '🏊'
  };

  // Genel turizm sektörü önerileri
  const generalRecommendations = [
    {
      category: 'Enerji Yönetimi',
      icon: '⚡',
      priority: 'Yüksek',
      recommendations: [
        'LED aydınlatma sistemlerine geçiş yapın',
        'Akıllı termostat sistemleri kullanın',
        'Güneş paneli kurulumu değerlendirin',
        'Enerji verimli klimalar tercih edin',
        'Sensörlü aydınlatma sistemleri kurun',
        'Isı yalıtımını iyileştirin'
      ],
      potentialSaving: '20-40% enerji tasarrufu'
    },
    {
      category: 'Su Yönetimi',
      icon: '💧',
      priority: 'Yüksek',
      recommendations: [
        'Düşük akışlı duş başlıkları kullanın',
        'Yağmur suyu toplama sistemi kurun',
        'Gri su geri dönüşüm sistemi kurulumu',
        'Peyzaj sulamasında damlama sulama tercih edin',
        'Su kaçağı tespit sistemleri kurun',
        'Misafirleri su tasarrufu konusunda bilgilendirin'
      ],
      potentialSaving: '15-30% su tasarrufu'
    },
    {
      category: 'Atık Yönetimi',
      icon: '🗑️',
      priority: 'Orta',
      recommendations: [
        'Kompost sistemi oluşturun',
        'Plastik kullanımını azaltın',
        'Geri dönüştürülebilir malzemeler tercih edin',
        'Tek kullanımlık ürünleri elimine edin',
        'Atık ayrıştırma sistemleri kurün',
        'Yemek israfını minimize edin'
      ],
      potentialSaving: '25-50% atık azaltımı'
    },
    {
      category: 'Ulaşım',
      icon: '🚗',
      priority: 'Orta',
      recommendations: [
        'Elektrikli servis araçları kullanın',
        'Bisiklet kiralama hizmeti sunun',
        'Toplu taşıma koordinasyonu yapın',
        'Karpul sistemleri teşvik edin',
        'Yakın mesafe yürüyüş rotaları oluşturun',
        'Uzak mesafe seyahatlerde tren seçeneklerini sunun'
      ],
      potentialSaving: '10-25% ulaşım emisyonu azaltımı'
    },
    {
      category: 'Yemek & İçecek',
      icon: '🍽️',
      priority: 'Orta',
      recommendations: [
        'Yerel ve organik ürünler tercih edin',
        'Mevsimsel menüler oluşturun',
        'Vegetaryen/vegan seçenekleri artırın',
        'Yemek porsiyonlarını optimize edin',
        'Sürdürülebilir deniz ürünleri seçin',
        'Kompost programı başlatın'
      ],
      potentialSaving: '15-35% F&B emisyonu azaltımı'
    }
  ];

  // Sektör karşılaştırmaları ve hedefler
  const industryBenchmarks = {
    greenKey: {
      name: 'Green Key Sertifikasyonu',
      levels: [
        { name: 'Bronze', target: 20, description: 'Temel çevre standartları' },
        { name: 'Silver', target: 15, description: 'Gelişmiş sürdürülebilirlik' },
        { name: 'Gold', target: 12, description: 'Üstün çevre performansı' },
        { name: 'Platinum', target: 8, description: 'Dünya standartında sürdürülebilirlik' }
      ]
    },
    leed: {
      name: 'LEED Sertifikasyonu',
      levels: [
        { name: 'Certified', target: 18, description: 'Temel yeşil bina standardı' },
        { name: 'Silver', target: 14, description: 'İyi çevre performansı' },
        { name: 'Gold', target: 10, description: 'Yüksek sürdürülebilirlik' },
        { name: 'Platinum', target: 7, description: 'En yüksek çevre standardı' }
      ]
    }
  };

  // Trendleri analiz et
  const trendAnalysis = useMemo(() => {
    if (!history || history.length < 3) {
      return { trend: 'insufficient', message: 'Trend analizi için en az 3 hesaplama gerekli' };
    }

    const recentCalculations = history
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5);

    const totals = recentCalculations.map(calc => calc.total);
    const avgChange = totals.length > 1 ? 
      (totals[0] - totals[totals.length - 1]) / (totals.length - 1) : 0;

    let trend = 'stable';
    let message = 'Emisyonlarınız stabil seyir izliyor';
    let recommendations = [];

    if (avgChange > 2) {
      trend = 'increasing';
      message = 'Emisyonlarınız artış eğiliminde';
      recommendations = [
        'Acil olarak enerji tüketimini kontrol edin',
        'Operasyonel değişiklikleri gözden geçirin',
        'Yeni ekipmanların enerji verimliliğini değerlendirin'
      ];
    } else if (avgChange < -2) {
      trend = 'decreasing';
      message = 'Tebrikler! Emisyonlarınız azalış eğiliminde';
      recommendations = [
        'Mevcut iyileştirmeleri sürdürün',
        'Başarılı uygulamaları diğer alanlara yaygınlaştırın',
        'Hedefleri daha da iddialı hale getirin'
      ];
    } else {
      recommendations = [
        'Yeni iyileştirme alanları belirleyin',
        'Benchmark analizi yapın',
        'Personel eğitimlerini artırın'
      ];
    }

    return { trend, message, recommendations, avgChange };
  }, [history]);

  const handleSectionToggle = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getCurrentPerformance = () => {
    if (!calculation) return null;

    const perRoomNight = calculation.perRoomNight;
    let performance = 'poor';
    let message = 'Emisyonlar yüksek seviyede';
    let color = 'error';

    if (perRoomNight <= 8) {
      performance = 'excellent';
      message = 'Mükemmel çevre performansı';
      color = 'success';
    } else if (perRoomNight <= 12) {
      performance = 'good';
      message = 'İyi çevre performansı';
      color = 'success';
    } else if (perRoomNight <= 18) {
      performance = 'average';
      message = 'Ortalama performans';
      color = 'warning';
    }

    return { performance, message, color };
  };

  const renderPriorityChip = (priority) => (
    <Chip
      label={priority === 'Yüksek' ? '⚠️ ' + priority : 
            priority === 'Orta' ? 'ℹ️ ' + priority : 
            '✅ ' + priority}
      color={priority === 'Yüksek' ? 'error' : priority === 'Orta' ? 'warning' : 'success'}
      size="small"
    />
  );

  const performance = getCurrentPerformance();

  return (
    <Box>
      {/* Mevcut Hesaplama Önerileri */}
      {calculation && (
        <Accordion expanded={expandedSections.current} onChange={() => handleSectionToggle('current')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <CategoryHeader>
              <LightbulbIcon color="primary" />
              <Typography variant="h6">Mevcut Hesaplama İçin Öneriler</Typography>
              {performance && (
                <Chip label={performance.message} color={performance.color} size="small" />
              )}
            </CategoryHeader>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              {calculation.recommendations && calculation.recommendations.map((rec, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <RecommendationCard priority={rec.priority}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <span style={{ fontSize: '1.5rem' }}>
                          {categoryIcons[rec.category] || '💡'}
                        </span>
                        <Typography variant="h6">{rec.category}</Typography>
                        {renderPriorityChip(rec.priority)}
                      </Box>
                      <Typography variant="body2" paragraph>
                        {rec.suggestion}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <span style={{ color: 'green' }}>💰</span>
                        <Typography variant="caption" color="success.main">
                          Potansiyel tasarruf: {rec.potentialSaving} kg CO₂e
                        </Typography>
                      </Box>
                    </CardContent>
                  </RecommendationCard>
                </Grid>
              ))}
            </Grid>

            {/* Performans Göstergesi */}
            {performance && (
              <Paper sx={{ p: 2, mt: 2, backgroundColor: `${performance.color}.light` }}>
                <Typography variant="h6" gutterBottom>
                  📈 Performans Değerlendirmesi
                </Typography>
                <Typography variant="body1" paragraph>
                  Oda-gece başına <strong>{Math.round(calculation.perRoomNight)} kg CO₂e</strong> emisyon ile {performance.message.toLowerCase()}.
                </Typography>
                
                {/* Benchmark karşılaştırması */}
                <Typography variant="subtitle2" gutterBottom>
                  Sertifikasyon Hedefleri:
                </Typography>
                <Box mb={2}>
                  {industryBenchmarks.greenKey.levels.map((level) => {
                    const achieved = calculation.perRoomNight <= level.target;
                    return (
                      <Box key={level.name} display="flex" alignItems="center" gap={1} mb={1}>
                        <CheckIcon color={achieved ? 'success' : 'disabled'} fontSize="small" />
                        <Typography 
                          variant="body2" 
                          color={achieved ? 'success.main' : 'text.secondary'}
                        >
                          Green Key {level.name}: ≤ {level.target} kg CO₂e ({level.description})
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Genel Turizm Önerileri */}
      <Accordion expanded={expandedSections.general} onChange={() => handleSectionToggle('general')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CategoryHeader>
            <EcoIcon color="success" />
            <Typography variant="h6">Genel Sürdürülebilirlik Önerileri</Typography>
          </CategoryHeader>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {generalRecommendations.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      {category.icon}
                      <Typography variant="h6">{category.category}</Typography>
                      {renderPriorityChip(category.priority)}
                    </Box>
                    
                    <List dense>
                      {category.recommendations.map((rec, recIndex) => (
                        <ListItem key={recIndex} sx={{ pl: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>
                              {recIndex + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText primary={rec} />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" alignItems="center" gap={1}>
                      <StarIcon color="warning" fontSize="small" />
                      <Typography variant="caption" color="text.secondary">
                        {category.potentialSaving}
                      </Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Sektör Karşılaştırmaları */}
      <Accordion expanded={expandedSections.industry} onChange={() => handleSectionToggle('industry')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CategoryHeader>
            <StarIcon color="warning" />
            <Typography variant="h6">Sektör Standartları ve Sertifikasyonlar</Typography>
          </CategoryHeader>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {Object.entries(industryBenchmarks).map(([key, benchmark]) => (
              <Grid item xs={12} md={6} key={key}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      🏆 {benchmark.name}
                    </Typography>
                    
                    {benchmark.levels.map((level, index) => {
                      const achieved = calculation ? calculation.perRoomNight <= level.target : false;
                      const progress = calculation ? 
                        Math.min((level.target / calculation.perRoomNight) * 100, 100) : 0;
                      
                      return (
                        <Box key={level.name} mb={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="subtitle2">
                              {level.name} (≤ {level.target} kg CO₂e)
                            </Typography>
                            <Chip
                              label={achieved ? 'Ulaşıldı' : 'Hedef'}
                              color={achieved ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={achieved ? 100 : progress}
                            color={achieved ? 'success' : 'primary'}
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {level.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Trend Analizi */}
      <Accordion expanded={expandedSections.trends} onChange={() => handleSectionToggle('trends')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <CategoryHeader>
            <SaveIcon color="info" />
            <Typography variant="h6">Trend Analizi ve Gelecek Hedefleri</Typography>
          </CategoryHeader>
        </AccordionSummary>
        <AccordionDetails>
          <Alert 
            severity={trendAnalysis.trend === 'increasing' ? 'warning' : 
                     trendAnalysis.trend === 'decreasing' ? 'success' : 'info'}
            sx={{ mb: 2 }}
          >
            <Typography variant="h6">{trendAnalysis.message}</Typography>
            {trendAnalysis.avgChange !== undefined && (
              <Typography variant="body2">
                Ortalama değişim: {trendAnalysis.avgChange > 0 ? '+' : ''}{Math.round(trendAnalysis.avgChange * 100) / 100} kg CO₂e
              </Typography>
            )}
          </Alert>

          {trendAnalysis.recommendations && (
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📋 Trend Bazlı Öneriler
                </Typography>
                <List>
                  {trendAnalysis.recommendations.map((rec, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <IdeaIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </StyledCard>
          )}

          {/* Gelecek Hedefleri */}
          <StyledCard sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Önerilen Hedefler
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.light' }}>
                    <Typography variant="h5" color="success.main">-10%</Typography>
                    <Typography variant="body2">Kısa Vadeli (3 ay)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'warning.light' }}>
                    <Typography variant="h5" color="warning.main">-25%</Typography>
                    <Typography variant="body2">Orta Vadeli (1 yıl)</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'primary.light' }}>
                    <Typography variant="h5" color="primary.main">-50%</Typography>
                    <Typography variant="body2">Uzun Vadeli (3 yıl)</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </StyledCard>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TourismRecommendations;
